/**
 * src/components/shared/LeadSyncButton.jsx — top-bar "scrape Gmail RFQs" trigger.
 *
 * CEO request 2026-06-15: a one-tap button sitting next to the airplane-mode
 * control to manually run the Gmail enquiry (RFQ) sync from anywhere, instead
 * of only from the agent chat header. Reuses the same API (syncGmailLeads) and
 * i18n keys as ChatHeader's sync-leads button; the server gates the action to
 * ceo/admin and returns the structured { ok, created, skipped } summary.
 */

import { useState, useCallback } from 'react';
import { Mail, Loader } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { useToast } from './ToastProvider';
import { syncGmailLeads } from '../../lib/api/leads.js';

/** The server's "source not wired" signal (lib/leads/emailSource.js) — distinct
 *  from a transient failure: no amount of retrying configures the mailbox. */
function isNotConfigured(error) {
  if (!error || typeof error !== 'string') return false;
  return /not configured|GMAIL_SA_KEY|GMAIL_LEAD_MAILBOX/i.test(error);
}

export default function LeadSyncButton() {
  const { t } = useT();
  const { showSuccess, showError } = useToast();
  const [syncing, setSyncing] = useState(false);

  const onSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await syncGmailLeads({});
      if (res?.ok) {
        showSuccess(t('agentChat.syncLeadsResult', {
          created: res.created ?? 0,
          skipped: res.skipped ?? 0,
        }));
        // Lead sync creates deals SERVER-side, bypassing the client mutation
        // path, so the pipeline never refetched (CEO 2026-06-17: new lead only
        // showed after a manual reload). Signal the data layer to silent-reload.
        window.dispatchEvent(new CustomEvent('acn:crm-refresh'));
      } else if (isNotConfigured(res?.error)) {
        // The server has no Gmail source wired (GMAIL_SA_KEY / GMAIL_LEAD_MAILBOX
        // unset). Retrying can NEVER help — tell the truth instead of the
        // generic "failed — please try again". TICKET-205.
        showError(t('agentChat.syncLeadsNotConfigured'));
      } else {
        showError(t('agentChat.syncLeadsError'));
      }
    } catch (err) {
      console.warn('[LeadSyncButton] lead sync failed:', err?.message);
      showError(t('agentChat.syncLeadsError'));
    } finally {
      setSyncing(false);
    }
  }, [syncing, showSuccess, showError, t]);

  return (
    <button
      data-testid="nav-lead-sync"
      onClick={onSync}
      disabled={syncing}
      aria-label={syncing ? t('agentChat.syncLeadsRunning') : t('agentChat.syncLeads')}
      aria-busy={syncing}
      title={syncing ? t('agentChat.syncLeadsRunning') : t('agentChat.syncLeads')}
      style={{
        width: 44, height: 44, minHeight: 44, borderRadius: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--accent-bg)', color: 'var(--accent)',
        // Inset hairline so the translucent-white --glass doesn't vanish on a
        // light/cream surface (TICKET-205). Radius-correct, no layout shift,
        // and rule-compliant (not a `border`). Harmless on dark.
        border: 'none', boxShadow: 'inset 0 0 0 1px var(--bdr)',
        cursor: syncing ? 'default' : 'pointer',
        flexShrink: 0, transition: 'background 150ms var(--ease)',
        opacity: syncing ? 0.6 : 1,
      }}
    >
      {syncing
        ? <Loader size={20} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />
        : <Mail size={20} strokeWidth={1.75} />}
    </button>
  );
}
